/*!
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
/* this comment is required to preserve license after minification */
import { FieldValue, FormIdentifier, FormValidationContext } from 'apmui/components/form/Model';
import { ControllerChain, Handler } from 'apmui/ControllerChain';
import EventTags from 'apmui/EventTags';
import IPageModel from 'apmui/page';
import { IResourceModel, IResourceLaunchEventDetail } from 'apmui/resource';
import { IDialogCloseEventDetail } from 'apmui/components/dialog/Model';
import merge from 'apmui/util/merge';
import Resolvable from 'apmui/util/Resolvable';

/**
 * App is the main application class that manages data, subscriptions and events.
 * Correct instance of the App could only be acquired by calling `App.get()`.
 */
class App extends ControllerChain<IPageModel> {
    static get(): App {
        if (App.instance === null) {
            App.instance = new App();
        }
        return App.instance;
    }
    private static instance: App | null = null;

    public data: IPageModel;
    public defaultController: Handler<IPageModel>;

    private constructor() {
        super({} as IPageModel);
        if (App.instance) {
            console.error('Overwriting existing Application instance');
        }
        App.instance = this;
    }

    /**
     * Set page configuration.
     *
     * @param config page configuration object
     */
    configure(config: Partial<IPageModel>) {
        this.data = merge(this.data, config);
    }

    /**
     * Revert form field values to defaults
     */
    resetForm(form: FormIdentifier) {
        this.dispatch(EventTags.FORM_RESET, form);
    }

    /**
     * Validate and submit form
     */
    submitForm(form: FormIdentifier) {
        this.dispatch(EventTags.FORM_VALIDATE, new FormValidationContext(form));
    }

    /**
     * Notify application about form field value change
     */
    updateFormField(form: FormIdentifier, name: string, newValue: FieldValue) {
        this.dispatch(EventTags.FORM_FIELD_CHANGED, {
            form,
            name,
            value: newValue,
        });
    }

    /**
     * Launch given resource
     */
    launchResource(
        resource: IResourceModel,
        options?: Pick<IResourceLaunchEventDetail, Exclude<keyof IResourceLaunchEventDetail, 'resource'>>
    ) {
        this.dispatch(EventTags.LAUNCH_RESOURCE, { ...options, resource });
    }

    /**
     * Show modal window.
     *
     * Note: Dialog controller must provide the result value if it's expected.
     * Rejection will happen by default.
     *
     * @param args object with additional arguments (specific for modal window class)
     * @returns Promise with the result of user's action
     */
    showModal<T = any>(dialogId: string, args?: object): Promise<T> {
        const callback = new Resolvable();
        this.dispatch(EventTags.DIALOG_OPEN, {
            ...args,
            callback,
            dialog: dialogId,
        });
        return callback.get();
    }

    /**
     * Close modal window
     * @param id    modal window class or id
     */
    closeModal(dialogClassOrId: IDialogCloseEventDetail) {
        this.dispatch(EventTags.DIALOG_CLOSE, dialogClassOrId);
    }
}

export default App;
